import { Subject } from 'rxjs';

interface ChannelOptions {
  signal?: AbortSignal;
  beforeConnect?: () => Promise<boolean>;
}
const pause = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      reject(new DOMException('Canceled', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', abort);
      resolve();
    }, ms);
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) abort();
  });

/** Cancelable initial handshakes and reconnects; no network queue during maintenance. */
export class WebSocketChannel {
  private socket?: WebSocket;
  private abort = new AbortController();
  private connecting?: Promise<void>;
  private backoff = 1000;
  private nextAttemptAt = 0;
  private readonly messages = new Subject<string>();
  readonly onMessage$ = this.messages.asObservable();
  private readonly closed = new Subject<void>();
  readonly onClose$ = this.closed.asObservable();
  private readonly reconnect = new Subject<void>();
  readonly onReconnect$ = this.reconnect.asObservable();
  private readonly connected = new Subject<void>();
  readonly onConnected$ = this.connected.asObservable();
  private externalAbort = () => this.close();

  static async create(url: string, options: ChannelOptions = {}): Promise<WebSocketChannel> {
    const channel = new WebSocketChannel(url, options);
    try {
      await channel.connect(false);
      return channel;
    } catch (error) {
      channel.close();
      throw error;
    }
  }
  private constructor(
    private url: string,
    private options: ChannelOptions
  ) {
    options.signal?.addEventListener('abort', this.externalAbort, { once: true });
    if (options.signal?.aborted) this.close();
  }
  get readyState() {
    return this.socket?.readyState ?? WebSocket.CLOSED;
  }
  send(message: object) {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }
  close() {
    this.abort.abort();
    this.options.signal?.removeEventListener('abort', this.externalAbort);
    this.socket?.close();
  }
  forceReconnect() {
    if (!this.abort.signal.aborted) this.socket?.close();
  }
  private connect(reconnecting: boolean): Promise<void> {
    this.connecting ??= this.connectLoop(reconnecting).finally(() => {
      this.connecting = undefined;
    });
    return this.connecting;
  }
  private async connectLoop(reconnecting: boolean) {
    while (!this.abort.signal.aborted) {
      if (this.options.beforeConnect && !(await this.options.beforeConnect())) {
        this.close();
        throw new DOMException('Maintenance', 'AbortError');
      }
      const wait = this.nextAttemptAt - Date.now();
      if (wait > 0) await pause(wait, this.abort.signal);
      if (this.abort.signal.aborted) break;
      if (reconnecting) this.reconnect.next();
      try {
        const socket = await this.handshake();
        if (this.abort.signal.aborted) {
          socket.close();
          break;
        }
        this.socket = socket;
        socket.addEventListener('message', (event) => this.messages.next(String(event.data)));
        const openedAt = Date.now();
        socket.addEventListener('close', (event) => {
          if (this.abort.signal.aborted) return;
          this.closed.next();
          if (Date.now() - openedAt >= 30_000) this.backoff = 1000;
          let requested = 0;
          if (event.code === 1013) {
            try {
              const seconds = Number(JSON.parse(event.reason).retryAfter);
              if (Number.isFinite(seconds) && seconds > 0)
                requested = Math.min(seconds, 86_400) * 1000;
            } catch {
              /* Handshake status/headers are unavailable in browsers. */
            }
          }
          this.schedule(Math.max(this.backoff, requested));
          void Promise.resolve(this.connecting)
            .then(() => this.connect(true))
            .catch(() => {});
        });
        this.connected.next();
        return;
      } catch (error) {
        if (this.abort.signal.aborted) throw error;
        this.schedule(this.backoff);
      }
    }
    throw new DOMException('Canceled', 'AbortError');
  }
  private schedule(delay: number) {
    this.nextAttemptAt = Date.now() + delay + 100 + Math.random() * Math.min(1000, delay * 0.1);
    this.backoff = Math.min(this.backoff * 2, 30_000);
  }
  private handshake(): Promise<WebSocket> {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(this.url);
      const done = () => {
        clearTimeout(timer);
        this.abort.signal.removeEventListener('abort', fail);
        socket.removeEventListener('error', fail);
        socket.removeEventListener('close', fail);
      };
      const fail = () => {
        done();
        socket.close();
        reject(new Error('WebSocket handshake failed'));
      };
      const timer = setTimeout(fail, 2000);
      this.abort.signal.addEventListener('abort', fail, { once: true });
      socket.addEventListener('error', fail, { once: true });
      socket.addEventListener('close', fail, { once: true });
      socket.addEventListener(
        'open',
        () => {
          done();
          resolve(socket);
        },
        { once: true }
      );
      if (this.abort.signal.aborted) fail();
    });
  }
}
